import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input, Modal, Select, Spin, Typography, message } from "antd";
import styled from "@emotion/styled";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import useProductTypeApi, { ProductTypeDto } from "apiClients/useProductTypeApiClient";
import { useLocaleStore } from "stores/localeStore";
import {
  PRODUCT_TYPE_COVERAGE_QUERY_KEY,
  PRODUCT_TYPES_QUERY_KEY,
  useProductTypeCoverageQuery,
  useProductTypesQuery,
} from "../hooks/useProductTypesQuery";
import {
  DropPos,
  applyMove,
  childrenOf,
  descendantIds,
  moveTarget,
  pathOf,
  rolledUpProductCount,
  siblingIndex,
} from "../productTypeTree";

const Page = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const TreeCard = styled.div`
  flex: 1 1 640px;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 24px;
`;

const Panel = styled.div`
  flex: 0 1 340px;
  min-width: 280px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 24px;
  position: sticky;
  top: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Title = styled(Typography.Title)`
  && {
    margin: 0;
    font-weight: 600;
    line-height: 1.4;
  }
`;

const CoverageCard = styled.div`
  background: #f7f8fa;
  border: 1px solid #eef0f3;
  border-radius: 8px;
  padding: 12px 16px 14px;
  margin-bottom: 16px;
`;

const CoverageTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #1f1f1f;
`;

const CoverageSummary = styled.span`
  color: #434343;
`;

const CoverageCount = styled.span`
  font-weight: 600;
`;

const CoverageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const MissingLink = styled(Link)`
  color: #1e4a6e;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-size: 13px;
  white-space: nowrap;
  &:hover {
    color: #2a5c85;
  }
`;

const CoveragePercent = styled.span`
  font-weight: 600;
  font-size: 13px;
  min-width: 36px;
  text-align: right;
`;

const BarTrack = styled.div`
  height: 8px;
  background: #e4e7ec;
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${(props) => props.$percent}%;
  background: #1e4a6e;
  border-radius: 999px;
`;

const GhostButton = styled.button`
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #1f1f1f;
  cursor: pointer;
  &:hover {
    border-color: #1e4a6e;
    color: #1e4a6e;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
`;

const SearchWrap = styled.div`
  flex: 1;
  display: flex;
  min-width: 0;
`;

const SearchInput = styled(Input)`
  border-radius: 6px 0 0 6px;
  height: 32px;
  font-size: 13px;
  &:hover,
  &:focus {
    border-color: #1e4a6e;
    box-shadow: none;
  }
`;

const SearchButton = styled.button`
  width: 46px;
  height: 32px;
  border: none;
  background: #1e4a6e;
  color: #fff;
  border-radius: 0 6px 6px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PrimaryButton = styled.button`
  height: 32px;
  padding: 0 16px;
  border: none;
  background: #1e4a6e;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.04);
  white-space: nowrap;
  &:hover {
    background: #2a5c85;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 104px;
  align-items: center;
  height: 46px;
  background: #f8f9fb;
  border-bottom: 1px solid #eef0f3;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 0 16px;
`;

const TreeRow = styled.div<{ $bg: string; $shadow: string; $opacity: number }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 104px;
  align-items: center;
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  cursor: pointer;
  background: ${({ $bg }) => $bg};
  box-shadow: ${({ $shadow }) => $shadow};
  opacity: ${({ $opacity }) => $opacity};
  user-select: none;
  &:hover {
    background: ${({ $bg }) => ($bg === "#fff" ? "#fafbfc" : $bg)};
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: ${({ $danger }) => ($danger ? "#ff4d4f" : "#1f1f1f")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover {
    background: ${({ $danger }) => ($danger ? "#fff1f0" : "#f0f0f0")};
  }
`;

const AddChildButton = styled(IconButton)`
  color: #1e4a6e;
  &:hover {
    background: #eef3f8;
  }
`;

const CountBadge = styled.span<{ $active: boolean }>`
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 7px;
  box-sizing: border-box;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? "#eef3f8" : "#f5f5f5")};
  color: ${({ $active }) => ($active ? "#1e4a6e" : "#bfbfbf")};
  font-size: 12px;
  line-height: 20px;
  text-align: center;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 12px 0;
  font-size: 13px;
  flex-wrap: wrap;
  gap: 8px;
`;

const DropZone = styled.div<{ $active: boolean }>`
  margin-top: 12px;
  height: 44px;
  border: 1px dashed ${({ $active }) => ($active ? "#1e4a6e" : "#bfbfbf")};
  background: ${({ $active }) => ($active ? "#e8f0f8" : "#fafafa")};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #1e4a6e;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
`;

const Switch = styled.button<{ $on: boolean }>`
  width: 44px;
  height: 22px;
  border: none;
  border-radius: 11px;
  background: ${({ $on }) => ($on ? "#1e4a6e" : "#bfbfbf")};
  position: relative;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  span {
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? "24px" : "2px")};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 4px rgba(0, 35, 11, 0.2);
    transition: left 0.2s;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 10px 16px;
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 13px;
  z-index: 1000;
`;

const Grip = () => (
  <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" aria-hidden>
    <circle cx="3" cy="3" r="1.4" />
    <circle cx="9" cy="3" r="1.4" />
    <circle cx="3" cy="8" r="1.4" />
    <circle cx="9" cy="8" r="1.4" />
    <circle cx="3" cy="13" r="1.4" />
    <circle cx="9" cy="13" r="1.4" />
  </svg>
);

const Folder = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.2l2 2.2h8.8A1.5 1.5 0 0 1 21 8.7v9.8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z" />
  </svg>
);

const EyeOff = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
    <path d="m2 2 20 20" />
  </svg>
);

type DropState = { id: number | "root"; pos: DropPos };
type ToastState = { text: string; undo?: () => Promise<void> };

const ProductTypeListView: React.FC = () => {
  const queryClient = useQueryClient();
  const { languages, activeLanguage } = useLocaleStore();
  const { data: productTypes = [], isLoading } = useProductTypesQuery();
  const { data: coverage } = useProductTypeCoverageQuery();
  const { createProductType, updateProductType, moveProductType, setProductTypeActive, deleteProductType } =
    useProductTypeApi();

  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [drop, setDrop] = useState<DropState | null>(null);
  const [editing, setEditing] = useState<{ id: number; value: string } | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [toast, setToast] = useState<ToastState | null>(null);
  const [busy, setBusy] = useState(false);
  const dragIdRef = useRef<number | null>(null);
  const dropRef = useRef<DropState | null>(null);
  const toastTimer = useRef<number>();
  const cancelEdit = useRef(false);
  const dirtyNames = useRef(new Set<number>());

  const queryKey = [PRODUCT_TYPES_QUERY_KEY, activeLanguage?.locale];

  const selected = productTypes.find((type) => type.id === selectedId) ?? null;
  const nameSignature = selected
    ? `${selected.id}|${selected.name}|${selected.translations.map((t) => `${t.languageId}:${t.name}`).join(",")}`
    : "";

  useEffect(() => {
    if (!selected) {
      setDrafts({});
      return;
    }
    const next: Record<number, string> = {};
    const source = languages.length ? languages : activeLanguage ? [activeLanguage] : [];
    source.forEach((language) => {
      const translation = selected.translations.find((item) => item.languageId === language.id);
      next[language.id] =
        translation?.name ?? (language.id === activeLanguage?.id ? selected.name : "");
    });
    dirtyNames.current.clear();
    setDrafts(next);
  }, [nameSignature, languages, activeLanguage, selected]);

  const showToast = (text: string, undo?: () => Promise<void>) => {
    window.clearTimeout(toastTimer.current);
    setToast({ text, undo });
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPE_COVERAGE_QUERY_KEY] });
  };

  const setDropState = (next: DropState | null) => {
    dropRef.current = next;
    setDrop(next);
  };

  const clearDrag = () => {
    dragIdRef.current = null;
    setDragId(null);
    setDropState(null);
  };

  const languageId = activeLanguage?.id ?? languages[0]?.id;

  const orderedLanguages = useMemo(() => {
    const source = languages.length ? languages : activeLanguage ? [activeLanguage] : [];
    return [...source].sort((a, b) => {
      if (a.id === activeLanguage?.id) return -1;
      if (b.id === activeLanguage?.id) return 1;
      return a.locale.localeCompare(b.locale);
    });
  }, [languages, activeLanguage]);

  const visibleIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const byId = new Map(productTypes.map((type) => [type.id, type]));
    const visible = new Set<number>();
    productTypes.forEach((type) => {
      const names = [type.name, ...type.translations.map((item) => item.name)];
      if (!names.some((name) => (name || "").toLowerCase().includes(q))) return;
      let current: ProductTypeDto | undefined = type;
      while (current) {
        visible.add(current.id);
        current = current.parentProductTypeId ? byId.get(current.parentProductTypeId) : undefined;
      }
    });
    return visible;
  }, [productTypes, query]);

  const rows = useMemo(() => {
    const result: ProductTypeDto[] = [];
    const walk = (parentId: number | null) => {
      childrenOf(productTypes, parentId).forEach((type) => {
        if (visibleIds && !visibleIds.has(type.id)) return;
        result.push(type);
        const open = !!(visibleIds || expanded[type.id]);
        if (open) walk(type.id);
      });
    };
    walk(null);
    return result;
  }, [productTypes, expanded, visibleIds]);

  const depthOf = (type: ProductTypeDto) => Math.max((type.level || 1) - 1, 0);

  const performMove = async (id: number, parentId: number | null, index: number) => {
    const current = productTypes.find((type) => type.id === id);
    if (!current) return;
    const previousParent = current.parentProductTypeId ?? null;
    const previousIndex = siblingIndex(productTypes, id);
    if (previousParent === parentId && previousIndex === index) return;

    const snapshot = queryClient.getQueryData<ProductTypeDto[]>(queryKey);
    queryClient.setQueryData<ProductTypeDto[]>(queryKey, (old) =>
      old ? applyMove(old, id, parentId, index) : old
    );
    if (parentId) setExpanded((state) => ({ ...state, [parentId]: true }));
    setSelectedId(id);

    try {
      await moveProductType(id, { parentProductTypeId: parentId, index });
      const parentName = parentId
        ? productTypes.find((type) => type.id === parentId)?.name || "överordnad typ"
        : "toppnivå";
      showToast(`"${current.name || "Produkttyp"}" flyttad till ${parentName}`, async () => {
        await moveProductType(id, { parentProductTypeId: previousParent, index: previousIndex });
        await refresh();
      });
      await refresh();
    } catch (error) {
      queryClient.setQueryData(queryKey, snapshot);
      message.error(error instanceof Error && error.message ? error.message : "Kunde inte flytta produkttypen");
    }
  };

  const addType = async (parentId: number | null) => {
    if (!languageId) {
      message.error("Inget språk är valt");
      return;
    }
    setBusy(true);
    try {
      const created = await createProductType({
        parentProductTypeId: parentId,
        translations: [{ languageId, name: "Ny produkttyp" }],
      });
      if (parentId) setExpanded((state) => ({ ...state, [parentId]: true }));
      setSelectedId(created.id);
      setEditing({ id: created.id, value: created.name || "Ny produkttyp" });
      setQuery("");
      showToast(`"${created.name || "Ny produkttyp"}" skapad`, async () => {
        await deleteProductType(created.id);
        if (selectedId === created.id) setSelectedId(null);
        await refresh();
      });
      await refresh();
    } catch (error) {
      message.error(error instanceof Error && error.message ? error.message : "Kunde inte skapa produkttyp");
    } finally {
      setBusy(false);
    }
  };

  const saveName = async (id: number, targetLanguageId: number, value: string) => {
    const trimmed = value.trim();
    const type = productTypes.find((item) => item.id === id);
    if (!type || !trimmed) return;
    const current =
      type.translations.find((item) => item.languageId === targetLanguageId)?.name ??
      (targetLanguageId === activeLanguage?.id ? type.name : "");
    if (current === trimmed) return;

    try {
      await updateProductType(id, {
        parentProductTypeId: type.parentProductTypeId ?? null,
        translations: [{ languageId: targetLanguageId, name: trimmed }],
      });
      showToast(`"${trimmed}" sparat`, async () => {
        await updateProductType(id, {
          parentProductTypeId: type.parentProductTypeId ?? null,
          translations: [{ languageId: targetLanguageId, name: current || trimmed }],
        });
        await refresh();
      });
      await refresh();
    } catch (error) {
      message.error(error instanceof Error && error.message ? error.message : "Kunde inte byta namn");
    }
  };

  const commitInline = async (id: number, value: string) => {
    setEditing(null);
    if (!languageId) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    await saveName(id, languageId, trimmed);
  };

  const toggleActive = async () => {
    if (!selected) return;
    const next = selected.isActive === false;
    const previous = selected.isActive !== false;
    try {
      await setProductTypeActive(selected.id, next);
      showToast(next ? `"${selected.name}" aktiverad` : `"${selected.name}" inaktiverad`, async () => {
        await setProductTypeActive(selected.id, previous);
        await refresh();
      });
      await refresh();
    } catch (error) {
      message.error(error instanceof Error && error.message ? error.message : "Kunde inte ändra status");
    }
  };

  const removeType = (id: number) => {
    const type = productTypes.find((item) => item.id === id);
    if (!type) return;
    const extra = descendantIds(productTypes, id).size;
    const products = rolledUpProductCount(productTypes, id);
    Modal.confirm({
      title: "Ta bort produkttyp",
      content: extra
        ? `Vill du ta bort "${type.name || "produkttypen"}" och ${extra} undertyper? ${products} produkter får ingen produkttyp.`
        : `Vill du ta bort "${type.name || "produkttypen"}"? ${products} produkter får ingen produkttyp.`,
      okText: "Ta bort",
      cancelText: "Avbryt",
      okType: "danger",
      onOk: async () => {
        try {
          await deleteProductType(id);
          const removed = new Set(descendantIds(productTypes, id));
          removed.add(id);
          if (selectedId != null && removed.has(selectedId)) setSelectedId(null);
          showToast(
            extra
              ? `"${type.name}" och ${extra} undertyper borttagna`
              : `"${type.name}" borttagen`
          );
          await refresh();
        } catch (error) {
          message.error(error instanceof Error && error.message ? error.message : "Fel vid borttagning av produkttyp");
        }
      },
    });
  };

  const parentOptions = useMemo(() => {
    if (!selected) return [];
    const blocked = descendantIds(productTypes, selected.id);
    blocked.add(selected.id);
    const options: { value: number; label: string }[] = [{ value: 0, label: "Toppnivå" }];
    const walk = (parentId: number | null, depth: number) => {
      childrenOf(productTypes, parentId).forEach((type) => {
        if (blocked.has(type.id)) return;
        options.push({
          value: type.id,
          label: `${"\u00A0\u00A0".repeat(depth * 2)}${type.name || `Typ #${type.id}`}`,
        });
        walk(type.id, depth + 1);
      });
    };
    walk(null, 1);
    return options;
  }, [productTypes, selected]);

  const totalProducts = coverage?.totalCount ?? 0;
  const withTypeCount = coverage?.withTypeCount ?? 0;
  const missingTypeCount = coverage?.missingTypeCount ?? 0;
  const coveragePercent =
    totalProducts === 0 ? 0 : Math.round((withTypeCount / totalProducts) * 100);
  const missingProductsHref = `/products?default-query=${encodeURIComponent(
    "productStatus::equals::0@@productTypeId::empty"
  )}`;

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Page>
        <TreeCard>
          <TitleRow>
            <Title level={4}>Produkttyper</Title>
            <div style={{ display: "flex", gap: 8 }}>
              <GhostButton
                type="button"
                onClick={() =>
                  setExpanded(Object.fromEntries(productTypes.map((type) => [type.id, true])))
                }
              >
                Expandera alla
              </GhostButton>
              <GhostButton type="button" onClick={() => setExpanded({})}>
                Fäll ihop alla
              </GhostButton>
            </div>
          </TitleRow>

          {coverage && (
            <CoverageCard>
              <CoverageTop>
                <CoverageSummary>
                  Produkter med produkttyp: <CoverageCount>{withTypeCount}</CoverageCount> av {totalProducts}
                </CoverageSummary>
                <CoverageMeta>
                  {missingTypeCount > 0 ? (
                    <MissingLink to={missingProductsHref}>{missingTypeCount} saknar typ</MissingLink>
                  ) : (
                    <span>0 saknar typ</span>
                  )}
                  <CoveragePercent>{coveragePercent}%</CoveragePercent>
                </CoverageMeta>
              </CoverageTop>
              <BarTrack>
                <BarFill $percent={coveragePercent} />
              </BarTrack>
            </CoverageCard>
          )}

          <Toolbar>
            <SearchWrap>
              <SearchInput
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Sök produkttyp..."
                allowClear
              />
              <SearchButton type="button" aria-label="Sök">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-4-4" />
                </svg>
              </SearchButton>
            </SearchWrap>
            <PrimaryButton type="button" disabled={busy} onClick={() => addType(null)}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
              Skapa produkttyp
            </PrimaryButton>
          </Toolbar>

          <HeaderRow>
            <div>NAMN</div>
            <div>ÅTGÄRDER</div>
          </HeaderRow>

          {rows.map((type) => {
            const childCount = childrenOf(productTypes, type.id).length;
            const open = !!(visibleIds || expanded[type.id]);
            const isDrop = drop?.id === type.id;
            const selectedRow = selectedId === type.id;
            const products = rolledUpProductCount(productTypes, type.id);
            const active = type.isActive !== false;
            const background =
              isDrop && drop?.pos === "inside" ? "#e8f0f8" : selectedRow ? "#f0f5fa" : "#fff";
            const shadow = !isDrop
              ? "none"
              : drop?.pos === "before"
                ? "inset 0 2px 0 #1e4a6e"
                : drop?.pos === "after"
                  ? "inset 0 -2px 0 #1e4a6e"
                  : "inset 0 0 0 1px #1e4a6e";

            return (
              <TreeRow
                key={type.id}
                draggable={editing?.id !== type.id}
                $bg={background}
                $shadow={shadow}
                $opacity={dragId === type.id ? 0.4 : 1}
                onClick={() => setSelectedId(type.id)}
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", String(type.id));
                  dragIdRef.current = type.id;
                  setDragId(type.id);
                }}
                onDragOver={(event) => {
                  const dragging = dragIdRef.current;
                  if (!dragging || dragging === type.id || descendantIds(productTypes, dragging).has(type.id)) {
                    if (dropRef.current) setDropState(null);
                    return;
                  }
                  event.preventDefault();
                  const rect = event.currentTarget.getBoundingClientRect();
                  const y = (event.clientY - rect.top) / rect.height;
                  const pos: DropPos = y < 0.28 ? "before" : y > 0.72 ? "after" : "inside";
                  if (dropRef.current?.id !== type.id || dropRef.current.pos !== pos) {
                    setDropState({ id: type.id, pos });
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const dragging = dragIdRef.current;
                  const currentDrop = dropRef.current;
                  clearDrag();
                  if (!dragging || !currentDrop || currentDrop.id === "root") return;
                  const target = moveTarget(productTypes, dragging, currentDrop.id, currentDrop.pos);
                  if (target) void performMove(dragging, target.parentId, target.index);
                }}
                onDragEnd={clearDrag}
              >
                <NameCell>
                  <span title="Dra för att flytta" style={{ color: "#bfbfbf", cursor: "grab", display: "flex", flexShrink: 0 }}>
                    <Grip />
                  </span>
                  <span style={{ width: depthOf(type) * 22, flexShrink: 0 }} />
                  {childCount > 0 ? (
                    <button
                      type="button"
                      aria-label={open ? "Fäll ihop" : "Expandera"}
                      onClick={(event) => {
                        event.stopPropagation();
                        setExpanded((state) => ({ ...state, [type.id]: !state[type.id] }));
                      }}
                      style={{
                        width: 20,
                        height: 20,
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#595959",
                        cursor: "pointer",
                        borderRadius: 4,
                        flexShrink: 0,
                      }}
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}
                      >
                        <path d="M3 1.5L7 5 3 8.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  ) : (
                    <span style={{ width: 20, flexShrink: 0 }} />
                  )}
                  <span style={{ color: childCount ? "#1e4a6e" : "#8c8c8c", display: "flex", flexShrink: 0 }}>
                    <Folder />
                  </span>
                  {editing?.id === type.id ? (
                    <input
                      autoFocus
                      value={editing.value}
                      onChange={(event) => setEditing({ id: type.id, value: event.target.value })}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                        if (event.key === "Escape") {
                          cancelEdit.current = true;
                          setEditing(null);
                          event.currentTarget.blur();
                        }
                      }}
                      onBlur={() => {
                        if (cancelEdit.current) {
                          cancelEdit.current = false;
                          return;
                        }
                        void commitInline(type.id, editing.value);
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: 28,
                        padding: "0 8px",
                        border: "1px solid #1e4a6e",
                        borderRadius: 6,
                        fontSize: 13,
                        outline: "none",
                        boxShadow: "0 0 0 2px rgba(30,74,110,0.12)",
                        userSelect: "text",
                      }}
                    />
                  ) : (
                    <>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontWeight: childCount ? 500 : 400,
                          color: active ? "#1f1f1f" : "#8c8c8c",
                        }}
                      >
                        {type.name || `Typ #${type.id}`}
                      </span>
                      {!active && (
                        <span title="Inaktiv" style={{ color: "#8c8c8c", display: "flex", flexShrink: 0 }}>
                          <EyeOff />
                        </span>
                      )}
                      <CountBadge
                        $active={products > 0}
                        title={childCount ? "Produkter totalt, inkl. undertyper" : "Produkter"}
                      >
                        {products}
                      </CountBadge>
                    </>
                  )}
                </NameCell>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }} onClick={(event) => event.stopPropagation()}>
                  <AddChildButton type="button" title="Lägg till undertyp" onClick={() => addType(type.id)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </AddChildButton>
                  <IconButton
                    type="button"
                    title="Byt namn"
                    onClick={() => {
                      cancelEdit.current = false;
                      setEditing({ id: type.id, value: type.name || "" });
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
                      <path d="M11 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6" />
                      <path d="M17.5 3.5l3 3L12 15l-4 1 1-4z" />
                    </svg>
                  </IconButton>
                  <IconButton $danger type="button" title="Ta bort" onClick={() => removeType(type.id)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
                      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
                    </svg>
                  </IconButton>
                </div>
              </TreeRow>
            );
          })}

          {rows.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "#8c8c8c", fontSize: 13 }}>
              {query.trim() ? "Inga produkttyper matchar sökningen" : "Inga produkttyper ännu"}
            </div>
          )}

          {dragId != null && (
            <DropZone
              $active={drop?.id === "root"}
              onDragOver={(event) => {
                event.preventDefault();
                if (dropRef.current?.id !== "root") setDropState({ id: "root", pos: "inside" });
              }}
              onDrop={(event) => {
                event.preventDefault();
                const dragging = dragIdRef.current;
                clearDrag();
                if (!dragging) return;
                const target = moveTarget(productTypes, dragging, null, "inside");
                if (target) void performMove(dragging, target.parentId, target.index);
              }}
            >
              Släpp här för att flytta till toppnivå
            </DropZone>
          )}

          <Footer>
            <div>
              Antal typer: <span style={{ fontWeight: 600 }}>{productTypes.length}</span>
            </div>
            <div style={{ color: "#8c8c8c" }}>Dra en rad för att flytta. Släpp ovanpå en rad för att lägga den under.</div>
          </Footer>
        </TreeCard>

        <Panel>
          {selected ? (
            <>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 6, lineHeight: 1.5 }}>
                {pathOf(productTypes, selected.id)}
              </div>
              <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20 }}>{selected.name || `Typ #${selected.id}`}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16, fontSize: 13 }}>
                {orderedLanguages.map((language, index) => (
                  <Field key={language.id}>
                    {index === 0 ? "Namn" : `Namn (${language.locale})`}
                    <Input
                      value={drafts[language.id] ?? ""}
                      maxLength={1024}
                      onChange={(event) => {
                        dirtyNames.current.add(language.id);
                        setDrafts((state) => ({ ...state, [language.id]: event.target.value }));
                      }}
                      onBlur={() => {
                        if (!dirtyNames.current.has(language.id)) return;
                        dirtyNames.current.delete(language.id);
                        const value = (drafts[language.id] ?? "").trim();
                        const stored =
                          selected.translations.find((item) => item.languageId === language.id)?.name ??
                          (language.id === activeLanguage?.id ? selected.name : "");
                        if (!value || value === stored) {
                          setDrafts((state) => ({ ...state, [language.id]: stored }));
                          return;
                        }
                        void saveName(selected.id, language.id, value);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                    />
                  </Field>
                ))}

                <Field>
                  Överordnad typ
                  <Select
                    value={selected.parentProductTypeId ?? 0}
                    options={parentOptions}
                    onChange={(value) => {
                      const parentId = value === 0 ? null : value;
                      if ((selected.parentProductTypeId ?? null) === parentId) return;
                      const index = childrenOf(productTypes, parentId).filter((type) => type.id !== selected.id).length;
                      void performMove(selected.id, parentId, index);
                    }}
                  />
                </Field>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  Status
                  <Switch
                    type="button"
                    $on={selected.isActive !== false}
                    role="switch"
                    aria-checked={selected.isActive !== false}
                    aria-label="Status"
                    onClick={() => void toggleActive()}
                  >
                    <span />
                  </Switch>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    padding: "12px 0",
                    borderTop: "1px solid #f0f0f0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div>
                    <div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 2 }}>Produkter</div>
                    <div style={{ fontSize: 16 }}>{rolledUpProductCount(productTypes, selected.id)}</div>
                  </div>
                  <div>
                    <div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 2 }}>Undertyper</div>
                    <div style={{ fontSize: 16 }}>{descendantIds(productTypes, selected.id).size}</div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <PrimaryButton type="button" style={{ flex: 1, justifyContent: "center" }} onClick={() => addType(selected.id)}>
                    + Lägg till undertyp
                  </PrimaryButton>
                  <button
                    type="button"
                    onClick={() => removeType(selected.id)}
                    style={{
                      height: 32,
                      padding: "0 14px",
                      border: "1px solid #ffccc7",
                      background: "#fff",
                      color: "#ff4d4f",
                      borderRadius: 6,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Ta bort
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ color: "#8c8c8c", fontSize: 13, textAlign: "center", padding: "24px 0" }}>
              Välj en produkttyp för att se detaljer
            </div>
          )}
        </Panel>
      </Page>

      {toast && (
        <Toast>
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#52c41a",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
            }}
          >
            ✓
          </span>
          <span>{toast.text}</span>
          {toast.undo && (
            <button
              type="button"
              onClick={() => {
                const undo = toast.undo;
                setToast(null);
                window.clearTimeout(toastTimer.current);
                void undo?.().catch(() => message.error("Kunde inte ångra"));
              }}
              style={{
                border: "none",
                background: "transparent",
                color: "#1e4a6e",
                fontWeight: 500,
                cursor: "pointer",
                fontSize: 13,
                padding: 0,
              }}
            >
              Ångra
            </button>
          )}
        </Toast>
      )}
    </>
  );
};

export default ProductTypeListView;
